import { useState, useMemo } from 'react'
import { Typography } from 'components/Typography'
import { Button, BUTTON_TYPES } from 'components/Button'
import { ReactComponent as Delete16 } from 'assets/svg/Delete16.svg'
import { ROLES_NAMES } from 'constants/common'
import { LED_BY_VALUE as LBV } from 'components/LeadByIndicator/constants'
import { Avatar } from 'components/Avatar/Avatar'
import { ACTIONS, ROLES, SUBJECTS } from 'features/Permission'
import { Props } from './types'
import { useContractCategoryDetailsAbility } from '../../../Providers/ContractCategoryDetailsAbilityProvider'
import { useContractCategoryDetailsContext } from '../../../Providers/ContractCategoryDetailsContextProvider'
import { contractCategorySubject } from '../../../Abilities'
import { useContractCategoryDetailsPopup } from '../../../Providers/ContractCategoryDetailsPopupProvider'
import { Reassign } from '../../../../Reassign/ReassignPopup'
import { ReactComponent as ReassignIcon } from 'assets/svg/Reassign.svg'
import { useSelector } from 'react-redux'
import { getUser } from '../../../../../redux/store/user/getters'
import './styles.scss'
import { Chips } from '../../../../../components/Chips/Chips'

export const UserListItem = (props: Props) => {
  const { user, sourcingOwnerUser } = props
  const currentUser = useSelector(getUser)
  const ability = useContractCategoryDetailsAbility()
  const ccDetailsContext = useContractCategoryDetailsContext()
  const { deleteResponsibleUserPopup } = useContractCategoryDetailsPopup()
  const [isOpenReassign, setIsOpenReassign] = useState(false)

  const { details, isCommons } = ccDetailsContext.state
  const { getCCDetailsAsync } = ccDetailsContext.actions
  const { open } = deleteResponsibleUserPopup.actions
  const name = user.first_name + ' ' + user.last_name
  const capitalCharName = `${user.first_name?.charAt(0) || ''}${
    user.last_name?.charAt(0) || ''
  }`.toUpperCase()

  const onDelete = () => {
    if (user) {
      open({ user })
    }
  }

  const isHsGPOLedBy = [LBV.HEALTH_SYSTEM, LBV.NATIONAL_GPO].includes(
    details.led_by
  )

  const isCommonsLedBy = [
    LBV.OM_LIFELINE,
    LBV.LIFELINE_FOR_COMMUNITY,
    LBV.LIFELINE_FOR_HEALTH_SYSTEM
  ].includes(details.led_by)

  const canDeleteResponsibleUser = useMemo(
    () =>
      ability.can(
        ACTIONS.DELETE,
        contractCategorySubject(SUBJECTS.RESPONSIBLE_USER, details)
      ) &&
      (user.role === ROLES.ANALYST ||
        user.role === ROLES.LEGAL ||
        user.role === ROLES.COMMONS_LEGAL ||
        user.role === ROLES.COMMONS_ANALYST),
    [ability, details, user.role]
  )

  const canReassignOwner = useMemo(
    () =>
      ability.can(
        ACTIONS.EDIT,
        contractCategorySubject(SUBJECTS.OWNER, details)
      ) &&
      (currentUser.role === ROLES.VICE_PRESIDENT ? isHsGPOLedBy : true) &&
      user.role !== ROLES.ANALYST &&
      user.role !== ROLES.LEGAL &&
      user.role !== ROLES.COMMONS_LEGAL &&
      user.role !== ROLES.COMMONS_ANALYST &&
      !(currentUser.community && isCommonsLedBy),
    [
      ability,
      currentUser.community,
      currentUser.role,
      details,
      isHsGPOLedBy,
      isCommonsLedBy,
      user.role
    ]
  )

  return (
    <div className="rfp-details-responsible-user row space-between full-width">
      <div className="rfp-details-responsible-user__user row align-center">
        <div className="rfp-details-responsible-user__avatar row align-center">
          <Avatar
            name={capitalCharName}
            alt="Responsible user image"
            src={user.avatar?.file_preview}
          />
        </div>
        <div className="rfp-details-responsible-user__content column space-between gap-2">
          <Typography.Body1 weight="semi-bold">{name}</Typography.Body1>
          <Typography.Caption className="rfp-details-responsible-user__role">
            {ROLES_NAMES[user.role]}
          </Typography.Caption>
          {sourcingOwnerUser === user.uuid && (
            <Chips
              closable={false}
              grey
              className="rfp-details-responsible-user__content__chips"
            >
              Sourcing owner
            </Chips>
          )}
        </div>
      </div>
      <div className="rfp-details-responsible-user__actions">
        {canDeleteResponsibleUser ? (
          <Button.Icon
            type={BUTTON_TYPES.LINK}
            icon={<Delete16 />}
            onClick={onDelete}
            small
            danger
          />
        ) : (
          canReassignOwner && (
            <Button.Icon
              type={BUTTON_TYPES.LINK}
              icon={<ReassignIcon />}
              onClick={() => setIsOpenReassign(true)}
              small
            />
          )
        )}
      </div>
      {canReassignOwner && (
        <Reassign
          isRFP={false}
          isContractCategory={true}
          isReassignPopupOpen={isOpenReassign}
          setIsReassignPopupOpen={setIsOpenReassign}
          ownerRole={user.role}
          ownerName={name}
          uuid={details?.uuid}
          isCommons={isCommons}
          update={getCCDetailsAsync}
        />
      )}
    </div>
  )
}
